const env = import.meta.env.VITE_IA_ENV || 'local';

export const auditorMediaQueries = {
  downLg: '(max-width: 1265px)',
  downXl: '(max-width: 1439px)',
  /** Phones: exactly where Tailwind's `sm:` (min-width: 40rem) stops applying. */
  downSm: 'not all and (min-width: 40rem)',
} as const;

export function envswitch<T>(vals: { local?: T; stg?: T; prod?: T; test?: T }): T {
  const envName =
    {
      staging: 'stg',
      production: 'prod',
    }[env] || env;
  const res = (vals as any)[envName];
  if (res === undefined) {
    throw new Error(`No value specified for env '${env}' in ${JSON.stringify(vals)}`);
  }
  return res;
}

export class ConfigBase {
  ENV = env;

  COMMIT_SHA = import.meta.env.VITE_COMMIT_SHA || 'local';
  BUILD_DATE = import.meta.env.VITE_BUILD_DATE || 'local';
  RELEASE_LABEL = this.BUILD_DATE == 'local' ? 'local' : `${this.BUILD_DATE}/${this.COMMIT_SHA.slice(0, 7)}`;
  COMMIT_SHA_URL = envswitch({
    local: null,
    // local: 'https://storage.googleapis.com/storage/v1/b/stg-food-auditor.rxdev.co/o/commit-sha.txt?alt=media',
    stg: 'https://storage.googleapis.com/storage/v1/b/stg-food-auditor.rxdev.co/o/commit-sha.txt?alt=media',
    prod: 'https://storage.googleapis.com/storage/v1/b/food-auditor.rxfoodapp.com/o/commit-sha.txt?alt=media',
  });

  IS_LOCAL = envswitch({
    local: true,
    stg: false,
    prod: false,
    test: false,
  });

  IS_DEV = envswitch({
    local: true,
    stg: true,
    prod: false,
    test: false,
  });

  IS_PROD = envswitch({
    local: false,
    stg: false,
    prod: true,
    test: false,
  });

  IS_TEST = envswitch({
    local: false,
    stg: false,
    prod: false,
    test: true,
  });

  IS_PREVIEW = window.location.host.includes('preview-');
}

/** Allowlisted API regions for dual-region auditor surfaces (FOOD-881). */
export enum ApiRegion {
  CA = 'ca',
  US = 'us',
}

export const parseApiRegion = (raw: string | null | undefined): ApiRegion | null =>
  raw === ApiRegion.CA || raw === ApiRegion.US ? raw : null;

/**
 * Suffix-shaped US MNT hosts. Live stg is stg-api-us.rxdev.co.
 * Do not use prefix us-api / us-stg-api; those names are unpublished.
 */
export const DEFAULT_US_API_URL = {
  stg: 'https://stg-api-us.rxdev.co',
  prod: 'https://api-us.rxfoodapp.com',
} as const;

export class IAFoodAuditorConfig extends ConfigBase {
  /** Canada (home) API. Default for the rest of the auditor. */
  API_URL =
    import.meta.env.VITE_API_URL
    || envswitch({
      local: 'http://localhost:1480',
      stg: 'https://stg-api.rxdev.co',
      prod: 'https://api.rxfoodapp.com',
    });

  /**
   * Suffix-shaped US hosts (same constructor as live helm / FOOD-880).
   * Local falls back to Canada API so staff-region-token can mint in-process.
   * US stg/prod may fail until DNS / US MNT exists; that is expected.
   * US calls use fetch on this host, not the shared axios client, so a US 401
   * cannot clear Canada auth_info.
   */
  US_API_URL =
    import.meta.env.VITE_US_API_URL
    || envswitch({
      local: import.meta.env.VITE_API_URL || 'http://localhost:1480',
      stg: DEFAULT_US_API_URL.stg,
      prod: DEFAULT_US_API_URL.prod,
    });

  /**
   * When US and Canada share a host, the US tab would mutate Canada meals.
   * Mutations stay off unless this explicit local dual-region flag is on.
   */
  ALLOW_LOCAL_DUAL_REGION_MUTATIONS = import.meta.env.VITE_DUAL_REGION_LOCAL === 'true';

  get isUsApiAliasedToCanada() {
    return this.US_API_URL === this.API_URL;
  }

  get usRegionalMutationsBlocked() {
    return this.isUsApiAliasedToCanada && !this.ALLOW_LOCAL_DUAL_REGION_MUTATIONS;
  }

  getApiUrlForRegion = (region: ApiRegion): string => (region === ApiRegion.US ? this.US_API_URL : this.API_URL);

  NOTIFICATION_ENGINE_URL =
    import.meta.env.VITE_NOTIFICATION_ENGINE_URL
    || envswitch({
      local: 'http://localhost:1489',
      stg: 'https://stg-notification-engine.rxdev.co',
      prod: 'https://notification-engine.rxfoodapp.com',
    });

  SENTRY_DSN = envswitch({
    local: 'https://4fe72352bf28444ca79d7c45e16fff30@o1345580.ingest.sentry.io/4504449337917440',
    stg: 'https://4fe72352bf28444ca79d7c45e16fff30@o1345580.ingest.sentry.io/4504449337917440',
    prod: 'https://745ee8a40ba34009b255e6ccdafb00aa@o1345580.ingest.sentry.io/4504442590396416',
  });

  FOOD_ENGINE_URL = envswitch({
    local: 'http://localhost:1430',
    stg: 'https://food-engine-zt4ajolhua-nn.a.run.app',
    prod: 'https://food-engine-em3etxqkxa-nn.a.run.app',
  });

  // Note: until the performance of the QA endpoint is improved, add this emergency
  // throttle switch to reduce the number of requests.
  EMERGENCY_THROTTLE_QA = true;
}

export const config = new IAFoodAuditorConfig();

/**
 * Cypress stubs never recover on a retry, and the 1s, 2s, 4s backoff dominates the route smoke test.
 * Dev builds only, so production never checks for Cypress and keeps the default retries.
 */
export const RETRY_FAILED_QUERIES = !(import.meta.env.DEV && typeof window !== 'undefined' && 'Cypress' in window);
